import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Group,
  Text,
  Avatar,
  Menu,
  ActionIcon,
  Burger,
  Box,
  UnstyledButton
} from '@mantine/core';
import { useMantineColorScheme } from '@mantine/core';
import {
  IconGift,
  IconSun,
  IconMoon,
  IconUser,
  IconSettings,
  IconMasksTheater,
  IconUsersGroup,
  IconHeart,
  IconLogout
} from '@tabler/icons-react';
import { useAuth } from "../contexts/AuthContext";
import { useSubscription } from "../contexts/SubscriptionContext";
import ProBadge from "./ProBadge";

const Navbar = ({ onMenuClick }) => {
  const { isAuthenticated, logout, user } = useAuth();
  const { isProUser, isFreeUser } = useSubscription();
  const { colorScheme, toggleColorScheme } = useMantineColorScheme();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (!isAuthenticated) return null;

  return (
    <Box
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: 'linear-gradient(135deg, #1971c2 0%, #1864ab 100%)',
        backdropFilter: 'blur(10px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
      }}
    >
      <Group 
        h={64} 
        px="xl" 
        justify="space-between"
        style={{ maxWidth: '100%' }}
      >
        {/* Left side - Logo and Burger */}
        <Group gap="md">
          <Burger
            opened={false}
            onClick={onMenuClick}
            color="white"
            size="sm"
            hiddenFrom="md"
          />
          <UnstyledButton 
            component={Link} 
            to="/"
            style={{ textDecoration: 'none' }}
          >
            <Group gap="xs">
              <Box
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0.1) 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}
              >
                <IconGift size={22} stroke={1.75} />
              </Box>
              <Box>
                <Text 
                  size="xl" 
                  fw={700} 
                  c="white"
                  style={{ 
                    lineHeight: 1.2,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                >
                  GiftPersona
                </Text>
                <Text 
                  size="xs" 
                  c="rgba(255,255,255,0.8)"
                  fw={500}
                  style={{ lineHeight: 1 }}
                >
                  AI-Powered Gift Discovery
                </Text>
              </Box>
            </Group>
          </UnstyledButton>
        </Group>

        {/* Right side - Theme Toggle and User Menu */}
        <Group gap="md">
          <ActionIcon
            variant="subtle"
            color={colorScheme === 'dark' ? 'gray' : 'blue'}
            onClick={() => toggleColorScheme()}
            title="Toggle color scheme"
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: 'white'
            }}
          >
            {colorScheme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
          </ActionIcon>

          <Menu
            shadow="lg" 
            width={220}
            position="bottom-end"
            offset={8}
          >
            <Menu.Target>
              <UnstyledButton
                style={{
                  padding: '6px 12px',
                  borderRadius: '12px',
                  transition: 'all 0.2s ease',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
                }}
              >
                <Group gap="sm">
                  <Avatar
                    size={32}
                    radius="xl"
                    color="cyan"
                    variant="gradient"
                    gradient={{ from: 'cyan', to: 'blue', deg: 45 }}
                    style={{ border: '2px solid rgba(255, 255, 255, 0.3)' }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                  </Avatar>
                  <Box visibleFrom="sm">
                    <Group gap="xs" align="center">
                      <Text 
                        size="sm" 
                        fw={600} 
                        c="white"
                        style={{ lineHeight: 1.2 }}
                      >
                        {user?.name || 'User'}
                      </Text>
                      {isProUser && <ProBadge size="xs" />}
                      {isFreeUser && (
                        <Box 
                          style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            background: 'rgba(255,255,255,0.15)',
                            border: '1px solid rgba(255,255,255,0.3)'
                          }}
                        >
                          <Text size="9px" fw={700} c="white" style={{ letterSpacing: '0.5px' }}>
                            FREE
                          </Text>
                        </Box>
                      )}
                    </Group>
                    <Text 
                      size="xs" 
                      c="rgba(255,255,255,0.7)"
                      style={{ lineHeight: 1 }}
                    >
                      {user?.email || ''}
                    </Text>
                  </Box>
                </Group>
              </UnstyledButton>
            </Menu.Target>

            <Menu.Dropdown style={{ border: '1px solid rgba(0,0,0,0.1)' }}>
              <Menu.Label>Account</Menu.Label>
              <Menu.Item
                leftSection={<IconUser size={15} />}
                onClick={() => navigate('/profile')}
              >
                My Persona
              </Menu.Item>
              <Menu.Item
                leftSection={<IconSettings size={15} />}
                onClick={() => navigate('/account')}
              >
                Settings
              </Menu.Item>

              <Menu.Divider />

              <Menu.Label>Quick Actions</Menu.Label>
              <Menu.Item
                leftSection={<IconMasksTheater size={15} />}
                onClick={() => navigate('/personas')}
              >
                My Personas
              </Menu.Item>
              <Menu.Item
                leftSection={<IconUsersGroup size={15} />}
                onClick={() => navigate('/groups')}
              >
                My Groups
              </Menu.Item>
              <Menu.Item
                leftSection={<IconHeart size={15} />}
                onClick={() => navigate('/wishlists')}
              >
                Wish Lists
              </Menu.Item>

              <Menu.Divider />

              <Menu.Item
                leftSection={<IconLogout size={15} />}
                color="red"
                onClick={handleLogout}
              >
                Logout
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
    </Box>
  );
};

export default Navbar;

export const MobileNavbar = () => {
  // This component is no longer used but kept for backward compatibility
  return null;
};
